import { useState, useEffect } from 'react';
import { collection, getDocs, query, where, orderBy } from 'firebase/firestore';
import { db } from '../config/firebase';
import {
  Users,
  Plane,
  FileText,
  CreditCard,
  TrendingUp,
  TrendingDown,
} from 'lucide-react';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

const Dashboard = () => {
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalTrips: 0,
    totalRequests: 0,
    totalTransactions: 0,
    activeTrips: 0,
    completedTrips: 0,
    cancelledTrips: 0,
    pendingTrips: 0,
    pendingRequests: 0,
    completedRequests: 0,
    cancelledRequests: 0,
    rejectedRequests: 0,
    acceptedRequests: 0,
    totalRevenue: 0,
    averageTripPrice: 0,
    requestAcceptanceRate: 0,
    newUsersLast30Days: 0,
    shippingTrips: 0,
    passengerTrips: 0,
    totalUserWalletBalances: 0,
    totalFlexyFees: 0,
  });
  const [loading, setLoading] = useState(true);
  const [chartData, setChartData] = useState([]);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      // Fetch users
      const usersSnapshot = await getDocs(collection(db, 'users'));
      const totalUsers = usersSnapshot.size;

      // Fetch trips
      const tripsSnapshot = await getDocs(collection(db, 'trips'));
      const trips = tripsSnapshot.docs.map(doc => {
        const data = doc.data();
        // Normalize status (same as in Trips.jsx)
        let tripStatus =
          data.status ||
          data.tripStatus ||
          data.state ||
          data.currentStatus ||
          null;

        // If status is still null/undefined, try to infer from other fields
        if (!tripStatus) {
          // Check departure date - if it's in the past, might be completed
          if (data.departureDate) {
            const departureDate = data.departureDate?.toDate
              ? data.departureDate.toDate()
              : new Date(data.departureDate);
            const now = new Date();
            // If departure date is more than 7 days ago, likely completed
            if (
              departureDate < now &&
              now - departureDate > 7 * 24 * 60 * 60 * 1000
            ) {
              tripStatus = 'completed';
            } else {
              tripStatus = 'active';
            }
          } else {
            tripStatus = 'active'; // Default fallback
          }
        }

        // Normalize status to lowercase and trim whitespace
        tripStatus = tripStatus?.toString().toLowerCase().trim() || 'active';

        return {
          id: doc.id,
          ...data,
          status: tripStatus, // Use normalized status
        };
      });
      const totalTrips = trips.length;
      const activeTrips = trips.filter(
        t => t.status?.toLowerCase() === 'active',
      ).length;
      const completedTrips = trips.filter(
        t => t.status?.toLowerCase() === 'completed',
      ).length;
      const cancelledTrips = trips.filter(
        t =>
          t.status?.toLowerCase() === 'cancelled' ||
          t.status?.toLowerCase() === 'canceled',
      ).length;
      const pendingTrips = trips.filter(
        t => t.status?.toLowerCase() === 'pending',
      ).length;
      // Helper function to determine trip type
      const isShippingTrip = tripType => {
        const normalized = tripType?.toLowerCase();
        return (
          normalized === 'shipping' ||
          normalized === 'offering' ||
          normalized === 'offeringShipping'
        );
      };

      const isPassengerTrip = tripType => {
        const normalized = tripType?.toLowerCase();
        return (
          normalized === 'carrying' ||
          normalized === 'looking' ||
          normalized === 'lookingForSpace' ||
          normalized === 'passenger' ||
          normalized === 'traveling'
        );
      };

      const shippingTrips = trips.filter(t =>
        isShippingTrip(t.tripType),
      ).length;
      const passengerTrips = trips.filter(t =>
        isPassengerTrip(t.tripType),
      ).length;

      // Fetch requests (needed for revenue calculation)
      const requestsSnapshot = await getDocs(collection(db, 'requests'));
      const requests = requestsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }));

      // Calculate revenue and average price from completed requests
      // Revenue should be calculated from completed requests, not trips,
      // because actual payments happen through requests
      let totalRevenue = 0;
      let priceCount = 0;

      // First, try to get revenue from completed requests (more accurate)
      const completedRequestsForRevenue = requests.filter(
        r => r.status?.toLowerCase() === 'completed',
      );

      completedRequestsForRevenue.forEach(request => {
        const requestPrice = request.price || request.estimatedPrice || 0;
        if (requestPrice > 0 && typeof requestPrice === 'number') {
          totalRevenue += requestPrice;
          priceCount++;
        }
      });

      // Also check completed trips for pricing (as backup)
      // But prioritize requests since that's where actual payments happen
      if (priceCount === 0) {
        trips.forEach(trip => {
          // Only count completed trips for revenue if we don't have request data
          if (trip.status?.toLowerCase() === 'completed') {
            if (trip.flatPrice && typeof trip.flatPrice === 'number') {
              totalRevenue += trip.flatPrice;
              priceCount++;
            } else if (trip.price && typeof trip.price === 'number') {
              totalRevenue += trip.price;
              priceCount++;
            } else if (
              trip.pricePerUnit &&
              typeof trip.pricePerUnit === 'number' &&
              trip.availableSpace &&
              typeof trip.availableSpace === 'number'
            ) {
              totalRevenue += trip.pricePerUnit * trip.availableSpace;
              priceCount++;
            }
          }
        });
      }

      const averageTripPrice = priceCount > 0 ? totalRevenue / priceCount : 0;
      const totalRequests = requests.length;
      const pendingRequests = requests.filter(
        r => r.status === 'pending',
      ).length;
      const completedRequests = requests.filter(
        r => r.status === 'completed',
      ).length;
      const cancelledRequests = requests.filter(
        r => r.status === 'cancelled',
      ).length;
      const rejectedRequests = requests.filter(
        r => r.status === 'rejected',
      ).length;
      const acceptedRequests = requests.filter(
        r => r.status === 'accepted',
      ).length;

      // Calculate request acceptance rate
      // Acceptance rate = (accepted + completed) / (total - pending)
      const totalProcessedRequests = totalRequests - pendingRequests;
      const requestAcceptanceRate =
        totalProcessedRequests > 0
          ? ((acceptedRequests + completedRequests) / totalProcessedRequests) *
            100
          : 0;

      // Fetch transactions and calculate fees (same logic as Fees.jsx)
      let totalTransactions = 0;
      let totalFlexyFees = 0;
      let totalUserWalletBalances = 0;

      try {
        const users = usersSnapshot.docs;

        // Calculate total wallet balances
        users.forEach(userDoc => {
          const userData = userDoc.data();
          const walletBalance = userData.walletBalance || 0;
          totalUserWalletBalances += walletBalance;
        });

        // Find fee account (flexyswap@gmail.com)
        const FEE_ACCOUNT_EMAIL = 'flexyswap@gmail.com';
        const feeAccountQuery = query(
          collection(db, 'users'),
          where('email', '==', FEE_ACCOUNT_EMAIL),
        );
        const feeAccountSnapshot = await getDocs(feeAccountQuery);

        if (!feeAccountSnapshot.empty) {
          // Calculate fees from fee account's transactions (same as Fees.jsx)
          const feeAccountDoc = feeAccountSnapshot.docs[0];
          const feeAccountId = feeAccountDoc.id;

          try {
            // Use orderBy to ensure all transactions are fetched (same as Fees.jsx)
            const feeTransactionsSnapshot = await getDocs(
              query(
                collection(
                  db,
                  'wallet_transactions',
                  feeAccountId,
                  'transactions',
                ),
                orderBy('date', 'desc'),
              ),
            );

            feeTransactionsSnapshot.docs.forEach(transDoc => {
              const transData = transDoc.data();

              // Check if it's a fee transaction (same logic as Fees.jsx)
              const isFeeTransaction =
                transData.type === 'earning' &&
                (transData.description
                  ?.toLowerCase()
                  .includes('flexyswap fee') ||
                  transData.description
                    ?.toLowerCase()
                    .includes('fee collected') ||
                  transData.description?.toLowerCase().includes('flexy fee'));

              if (isFeeTransaction) {
                const amount = Math.abs(transData.amount || 0);
                totalFlexyFees += amount;
              }
            });
          } catch (error) {
            console.warn('Error fetching fee account transactions:', error);
            // If orderBy fails (e.g., missing index), try without orderBy as fallback
            try {
              const feeTransactionsSnapshotFallback = await getDocs(
                collection(
                  db,
                  'wallet_transactions',
                  feeAccountId,
                  'transactions',
                ),
              );

              feeTransactionsSnapshotFallback.docs.forEach(transDoc => {
                const transData = transDoc.data();

                const isFeeTransaction =
                  transData.type === 'earning' &&
                  (transData.description
                    ?.toLowerCase()
                    .includes('flexyswap fee') ||
                    transData.description
                      ?.toLowerCase()
                      .includes('fee collected') ||
                    transData.description?.toLowerCase().includes('flexy fee'));

                if (isFeeTransaction) {
                  const amount = Math.abs(transData.amount || 0);
                  totalFlexyFees += amount;
                }
              });
            } catch (fallbackError) {
              console.warn('Fallback fetch also failed:', fallbackError);
            }
          }
        }

        // If no fee transactions found, calculate from completed requests (backup)
        if (totalFlexyFees === 0) {
          const completedRequestsForFees = requests.filter(
            r => r.status?.toLowerCase() === 'completed',
          );
          completedRequestsForFees.forEach(request => {
            const requestPrice = request.price || request.estimatedPrice || 0;
            if (requestPrice > 0) {
              // Flexy fee is 15% of the request price
              const calculatedFee = requestPrice * 0.15;
              totalFlexyFees += calculatedFee;
            }
          });
        }

        // Count total transactions from all users
        for (const userDoc of users) {
          try {
            const transactionsSnapshot = await getDocs(
              collection(db, 'wallet_transactions', userDoc.id, 'transactions'),
            );
            totalTransactions += transactionsSnapshot.size;
          } catch (error) {
            // Skip if user has no transactions collection
            console.warn(
              `Error fetching transactions for user ${userDoc.id}:`,
              error,
            );
          }
        }
      } catch (error) {
        console.error('Error fetching transactions:', error);
      }

      // Calculate new users in last 30 days
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      const newUsersLast30Days = usersSnapshot.docs.filter(doc => {
        const userData = doc.data();
        if (!userData.createdAt) return false;
        const userDate = userData.createdAt.toDate
          ? userData.createdAt.toDate()
          : new Date(userData.createdAt);
        return userDate >= thirtyDaysAgo;
      }).length;

      // Prepare chart data (last 7 days)
      const last7Days = [];
      const today = new Date();
      for (let i = 6; i >= 0; i--) {
        const date = new Date(today);
        date.setDate(date.getDate() - i);
        const dateStr = date.toISOString().split('T')[0];

        const dayTrips = trips.filter(t => {
          if (!t.createdAt) return false;
          const tripDate = t.createdAt.toDate?.() || new Date(t.createdAt);
          return tripDate.toISOString().split('T')[0] === dateStr;
        }).length;

        const dayRequests = requests.filter(r => {
          if (!r.createdAt) return false;
          const reqDate = r.createdAt.toDate?.() || new Date(r.createdAt);
          return reqDate.toISOString().split('T')[0] === dateStr;
        }).length;

        last7Days.push({
          date: date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
          }),
          trips: dayTrips,
          requests: dayRequests,
        });
      }

      setChartData(last7Days);
      setStats({
        totalUsers,
        totalTrips,
        totalRequests,
        totalTransactions,
        activeTrips,
        completedTrips,
        cancelledTrips,
        pendingTrips,
        pendingRequests,
        completedRequests,
        cancelledRequests,
        rejectedRequests,
        acceptedRequests,
        totalRevenue,
        averageTripPrice,
        requestAcceptanceRate,
        newUsersLast30Days,
        shippingTrips,
        passengerTrips,
        totalUserWalletBalances,
        totalFlexyFees,
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    {
      title: 'Total Users',
      value: stats.totalUsers,
      icon: Users,
      color: 'bg-blue-500',
      change: '+12%',
      trend: 'up',
    },
    {
      title: 'Total Trips',
      value: stats.totalTrips,
      icon: Plane,
      color: 'bg-green-500',
      change: '+8%',
      trend: 'up',
    },
    {
      title: 'Total Requests',
      value: stats.totalRequests,
      icon: FileText,
      color: 'bg-yellow-500',
      change: '+5%',
      trend: 'up',
    },
    {
      title: 'Total Transactions',
      value: stats.totalTransactions,
      icon: CreditCard,
      color: 'bg-purple-500',
      change: '+15%',
      trend: 'up',
    },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <div
              key={index}
              className="bg-white rounded-xl shadow-lg p-6 border-2 border-black"
            >
              <div className="flex items-center justify-between mb-4">
                <div className={`${stat.color} p-3 rounded-lg`}>
                  <Icon className="text-white" size={24} />
                </div>
                <div className="flex items-center space-x-1 text-sm">
                  {stat.trend === 'up' ? (
                    <TrendingUp className="text-green-500" size={16} />
                  ) : (
                    <TrendingDown className="text-red-500" size={16} />
                  )}
                  <span
                    className={
                      stat.trend === 'up' ? 'text-green-600' : 'text-red-600'
                    }
                  >
                    {stat.change}
                  </span>
                </div>
              </div>
              <h3 className="text-gray-600 text-sm font-medium mb-1">
                {stat.title}
              </h3>
              <p className="text-3xl font-bold text-gray-900">{stat.value}</p>
            </div>
          );
        })}
      </div>

      {/* Additional Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {/* Financial & Performance */}
        <div className="bg-white rounded-xl shadow-lg p-6 border-2 border-black">
          <h3 className="text-lg font-semibold text-black mb-4 border-b-2 border-yellow-500 pb-2">
            Financial & Performance
          </h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-gray-700">Total Revenue</span>
              <span className="text-2xl font-bold text-green-600">
                ${stats.totalRevenue.toFixed(2)}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-700">Avg Trip Price</span>
              <span className="text-xl font-bold text-blue-600">
                ${stats.averageTripPrice.toFixed(2)}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-700">Acceptance Rate</span>
              <span className="text-xl font-bold text-yellow-600">
                {stats.requestAcceptanceRate.toFixed(1)}%
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-700">Total User Wallets</span>
              <span className="text-2xl font-bold text-purple-600">
                ${stats.totalUserWalletBalances.toFixed(2)}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-700">Total Flexy Fees</span>
              <span className="text-2xl font-bold text-orange-600">
                ${stats.totalFlexyFees.toFixed(2)}
              </span>
            </div>
          </div>
        </div>

        {/* Trip Types */}
        <div className="bg-white rounded-xl shadow-lg p-6 border-2 border-black">
          <h3 className="text-lg font-semibold text-black mb-4 border-b-2 border-yellow-500 pb-2">
            Trip Types
          </h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-gray-700">Shipping</span>
              <span className="text-2xl font-bold text-purple-600">
                {stats.shippingTrips}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-700">Passenger</span>
              <span className="text-2xl font-bold text-indigo-600">
                {stats.passengerTrips}
              </span>
            </div>
          </div>
        </div>

        {/* User Growth */}
        <div className="bg-white rounded-xl shadow-lg p-6 border-2 border-black">
          <h3 className="text-lg font-semibold text-black mb-4 border-b-2 border-yellow-500 pb-2">
            User Growth
          </h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-gray-700">Total Users</span>
              <span className="text-2xl font-bold text-blue-600">
                {stats.totalUsers}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-700">New (30 days)</span>
              <span className="text-2xl font-bold text-green-600">
                {stats.newUsersLast30Days}
              </span>
            </div>
          </div>
        </div>

        {/* Transactions */}
        <div className="bg-white rounded-xl shadow-lg p-6 border-2 border-black">
          <h3 className="text-lg font-semibold text-black mb-4 border-b-2 border-yellow-500 pb-2">
            Transactions
          </h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-gray-700">Total</span>
              <span className="text-2xl font-bold text-purple-600">
                {stats.totalTransactions}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Charts */}
      <div className="bg-white rounded-xl shadow-lg p-6 border-2 border-black">
        <h3 className="text-lg font-semibold text-black mb-6 border-b-2 border-yellow-500 pb-2">
          Last 7 Days Activity
        </h3>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="date" />
            <YAxis />
            <Tooltip />
            <Legend />
            <Line
              type="monotone"
              dataKey="trips"
              stroke="#f97316"
              strokeWidth={2}
              name="Trips"
            />
            <Line
              type="monotone"
              dataKey="requests"
              stroke="#3b82f6"
              strokeWidth={2}
              name="Requests"
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      <div className="bg-white rounded-xl shadow-lg p-6 border-2 border-black">
        <h3 className="text-lg font-semibold text-black mb-6 border-b-2 border-yellow-500 pb-2">
          Daily Distribution
        </h3>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="date" />
            <YAxis />
            <Tooltip />
            <Legend />
            <Bar dataKey="trips" fill="#f97316" name="Trips" />
            <Bar dataKey="requests" fill="#3b82f6" name="Requests" />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default Dashboard;
